import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownContentProps {
  /** Markdown source. Raw HTML in it is not rendered, so untrusted text is safe to pass. */
  children: string;
  className?: string;
}

/**
 * Renders markdown with the application's typography. Used wherever a stored markdown field is
 * shown - the firmware release log and its editor preview - so both read the same.
 */
const MarkdownContent: React.FC<MarkdownContentProps> = ({ children, className }) => (
  // break-words so a long URL in a release log wraps instead of widening its container.
  <div className={`text-sm leading-relaxed break-words text-gray-800 ${className ?? ''}`}>
    <Markdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => <h1 className="mt-4 mb-2 text-lg font-semibold text-gray-900 first:mt-0">{children}</h1>,
        h2: ({ children }) => (
          <h2 className="mt-4 mb-2 text-base font-semibold text-gray-900 first:mt-0">{children}</h2>
        ),
        h3: ({ children }) => <h3 className="mt-3 mb-1 text-sm font-semibold text-gray-900 first:mt-0">{children}</h3>,
        p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0">{children}</p>,
        ul: ({ children }) => <ul className="my-2 list-disc space-y-1 pl-5">{children}</ul>,
        ol: ({ children }) => <ol className="my-2 list-decimal space-y-1 pl-5">{children}</ol>,
        li: ({ children }) => <li className="pl-1">{children}</li>,
        strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noreferrer" className="text-blue-600 underline hover:text-blue-800">
            {children}
          </a>
        ),
        blockquote: ({ children }) => (
          <blockquote className="my-2 border-l-4 border-gray-200 pl-3 text-gray-600 italic">{children}</blockquote>
        ),
        hr: () => <hr className="my-3 border-gray-200" />,
        pre: ({ children }) => (
          <pre className="my-2 overflow-x-auto rounded-md bg-gray-900 p-3 text-xs text-gray-100">{children}</pre>
        ),
        // Fenced blocks carry a language class and are already inside a styled <pre>;
        // anything without one is inline code.
        code: ({ className: codeClassName, children }) =>
          codeClassName ? (
            <code className={codeClassName}>{children}</code>
          ) : (
            <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em] text-gray-900">{children}</code>
          ),
        table: ({ children }) => (
          <div className="my-3 overflow-x-auto">
            <table className="w-full border-collapse text-left text-sm">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className="border-b border-gray-200 bg-gray-50">{children}</thead>,
        th: ({ children }) => <th className="px-3 py-2 font-semibold text-gray-700">{children}</th>,
        td: ({ children }) => <td className="border-b border-gray-100 px-3 py-2">{children}</td>
      }}
    >
      {children}
    </Markdown>
  </div>
);

export default MarkdownContent;
